import { restoreSection } from "../../../actions/DocumentActions";
import { DOCUMENT_COMPONENT_TYPES } from "../../../constants/document";
import {
  checkSectionIsLinkedQuoteTable,
  checkSectionIsTableV2GSheet,
  googleSheetSpace,
  emptyQuoteTablePlaceholder,
} from "../../../utils/documentUtils";
import { getFormFieldsDiffList } from "../../../utils/editorUtils";
import BaseCommand from "../../baseCommand";

class DeleteBaseCommand extends BaseCommand {
  constructor(componentData) {
    super();
    this.componentData = componentData;
  }

  _updateGoogleSheetComponent = componentData => {
    const { section } = componentData;
    if (section) {
      let updatedSection = section;
      if (section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
        updatedSection = {
          ...googleSheetSpace(),
          id: section.id,
          section_id: section.id,
          label: section.label,
        };
        updatedSection.removeGoogleSheet = true;
      } else if (
        section.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
        checkSectionIsTableV2GSheet(section)
      ) {
        updatedSection.data.attributes.isLinkedToDataSource = false;
        updatedSection.data.attributes.dataSource = "";
        updatedSection.removeGoogleSheet = true;
      } else {
        return null;
      }
      return updatedSection;
    }
    return null;
  };

  _updateDealTableComponent = componentData => {
    const { section } = componentData;
    if (section) {
      // update section
      let updatedSection = section;
      // if it is a quotetable component, convert it to a wrapper component
      if (
        section.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE ||
        section.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE
      ) {
        updatedSection = {
          ...emptyQuoteTablePlaceholder(),
          id: section.id,
          section_id: section.id,
          dealTableId: componentData.dealTableId,
        };
      } else if (
        // if table section if linked to quotetable, remove the link
        section.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
        checkSectionIsLinkedQuoteTable(section)
      ) {
        updatedSection.data.attributes.isLinkedToDataSource = false;
        updatedSection.data.attributes.dataSource = "";
        updatedSection.data.attributes.connected = false;
        updatedSection.connected = false;
      } else {
        // if it is not a quotetable component nor a table component
        // attached to quotetable, it does not need any update
        return null;
      }

      // now check if dealtable innstance needs to be deleted or not
      // now check if there is any other table linnked to quotetable or quotetable in this document
      // check if there is any other component attached to quotetable
      const { components } = componentData;
      const quoteTableSection = Object.keys(components).find(componentId => {
        const comp = components[componentId];
        if (
          componentId !== section.id &&
          (checkSectionIsLinkedQuoteTable(comp) ||
            comp.content.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE ||
            comp.content.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE)
        )
          return true;
        return false;
      });

      // if there is any other component available, no need to delete
      if (!quoteTableSection) {
        updatedSection.deleteDealTable = true;
        updatedSection.dealTableId = componentData.dealTableId;
      }

      return updatedSection;
    }
    return null;
  };

  updateAndDeleteSection = (updatedSection, section) =>
    new Promise((resolve, reject) => {
      this.componentData.updateSectionService(updatedSection).then(() => {
        this.componentData
          .deleteSectionService(this.componentData.docNo, updatedSection)
          .then(response => {
            if (Array.isArray(response) && response.length > 0) {
              this.componentData.updateComponentsDBRowColIndexesAction(
                response,
              );
            }
            resolve(response);
          })
          .catch(() => {
            this.componentData.addDocumentComponentAction(
              this.componentData.layout,
              section,
            );
            reject();
          });
      });
    });

  updateGeneralTables() {
    const { section } = this.componentData;
    if (
      section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET ||
      (section.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
        checkSectionIsTableV2GSheet(section))
    ) {
      this.updatedSection = this._updateGoogleSheetComponent(
        this.componentData,
      );
    } else if (
      section.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE || // check if it is a quote table
      section.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE || // or dealtable
      (section.type === DOCUMENT_COMPONENT_TYPES.TABLE && // or a table linked to quote table
        checkSectionIsLinkedQuoteTable(section))
    ) {
      this.updatedSection = this._updateDealTableComponent(this.componentData);
    }
  }

  resetAutoTitleFlagIfLastComponent = () => {
    const {
      components,
      isPdfDocument,
      mode,
      docID,
      updateAutoTitleFlagMetaValues,
    } = this.componentData;
    if (!isPdfDocument) {
      let size = Object.keys(components).length;
      if (size > 0) {
        Object.keys(components).forEach(key => {
          const comp = components[key];
          if (
            comp.content &&
            comp.content.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
          ) {
            size -= 1;
          }
        });
      }

      if (size <= 0) {
        if (updateAutoTitleFlagMetaValues) {
          updateAutoTitleFlagMetaValues(
            mode,
            docID,
            "should_update_title",
            true,
          );
        }
      }
    }
  };

  execute() {
    const { section, sectionID } = this.componentData;
    this.updatedSection = section;
    if (section) {
      this.updateGeneralTables();
      this.updatedSection.fieldDiffList = getFormFieldsDiffList(section, null);
    }
    // 1. delete document component action
    this.componentData.deleteDocumentComponentAction(
      sectionID,
      this.updatedSection,
    );
    // 2. runs in both document and template
    if (this.updatedSection) {
      this.updateAndDeleteSection(this.updatedSection, section)
        .then(() => {})
        .catch(() => {});
    } else {
      this.componentData
        .deleteSectionService(this.componentData.docNo, this.updatedSection)
        .then(() => {})
        .catch(() => {
          this.componentData.addDocumentComponentAction(
            this.componentData.layout,
            section,
          );
        });
    }
    this.resetAutoTitleFlagIfLastComponent();
  }

  undo() {
    const { section, headerFooterType, layout, mode, docNo, docID } =
      this.componentData;

    // Scroll section into view when do Undo
    if (section) {
      this.scrollIntoView(section.id);
    }

    // check for the deleted dynamic fields
    section.fieldDiffList = getFormFieldsDiffList(null, section);
    restoreSection(mode)(docNo || docID, section, layout);

    this.componentData.addDocumentComponentAction(
      layout,
      section,
      headerFooterType,
      true,
    );
  }
}

export default DeleteBaseCommand;
